import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { motion } from "framer-motion";

const ALL_CATS = [
  "All",
  "Hair Care",
  "Skin Care",
  "Facial Kits",
  "Serums",
  "Wax Products",
  "Professional Salon Essentials",
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("cat") || "All";
  const initialQuery = params.get("q") || "";
  const [cat, setCat] = useState(initialCat);
  const [query, setQuery] = useState(initialQuery);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCat(params.get("cat") || "All");
    setQuery(params.get("q") || "");
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const p = cat === "All" ? {} : { category: cat };
    api.get("/products", { params: p }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [cat]);

  const change = (c) => {
    const next = {};
    if (c !== "All") next.cat = c;
    if (query) next.q = query;
    setParams(next);
  };

  const clearSearch = () => {
    const next = {};
    if (cat !== "All") next.cat = cat;
    setParams(next);
  };

  // Apply client-side search filter on top of fetched products
  const filtered = query
    ? products.filter((p) => {
        const q = query.trim().toLowerCase();
        return (
          (p.name || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q)
        );
      })
    : products;

  return (
    <div className="pt-32 pb-20 min-h-screen" data-testid="shop-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center gap-4 justify-center mb-4">
            <span className="divider-gold" />
            <span className="overline">The Shop</span>
            <span className="divider-gold" />
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl tracking-tighter font-light">
            {query
              ? <>Results for <em className="gold-text not-italic">"{query}"</em></>
              : cat === "All"
                ? <>The Full <em className="gold-text not-italic">Collection</em></>
                : cat}
          </h1>
          <p className="text-base text-[#5C5C5C] mt-4">
            {query
              ? `${filtered.length} product${filtered.length === 1 ? "" : "s"} found`
              : cat === "All" ? "Every Blisson ritual, in one place." : `Discover our ${cat.toLowerCase()} range.`}
          </p>
          {query && (
            <button
              onClick={clearSearch}
              className="mt-4 text-xs uppercase tracking-[0.2em] text-[#5C5C5C] hover:text-black underline underline-offset-4"
              data-testid="shop-clear-search"
            >
              Clear search ×
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar mb-12 justify-start md:justify-center pb-2" data-testid="category-filters">
          {ALL_CATS.map((c) => (
            <button
              key={c}
              onClick={() => change(c)}
              className={`px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap transition-all border ${
                cat === c
                  ? "bg-black text-white border-black"
                  : "bg-transparent text-black border-black/15 hover:border-black"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center overline py-20" data-testid="shop-loading">Loading the collection…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 font-serif-display text-2xl" data-testid="shop-empty">
            {query ? `No products match "${query}".` : "No products in this category yet."}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8" data-testid="shop-grid">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
              >
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
